import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { WorkbenchStatusEnum } from '~/api/types';
import {
  useGetProjectQuery,
  useLazyGetConnectToWorkbenchQuery,
  useStartWorkbenchMutation,
  useStopWorkbenchMutation,
  useTerminateWorkbenchMutation,
} from '~/api/v2/hooks';
import { Tooltip } from '~/components';
import { ConnectionInstructionsProps } from '~/components/ConnectionInstructions/ConnectionInstructions';
import { DEFAULT_PORT } from '~/components/ConnectionInstructions/types';
import { InstructionsIcon, StartIcon, StopIcon, TerminateIcon, WarningIcon } from '~/icons';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { getPriviligies } from '~/utils';

interface WorkbenchControlCellProps {
  status: WorkbenchStatusEnum;
  workbenchName: string;
  workbenchId: number;
  instanceId: string;
}

const WorkbenchControlCell: FC<WorkbenchControlCellProps> = ({ status, workbenchName, workbenchId, instanceId }) => {
  const dispatch = useAppDispatch();
  const projectId = +useParams().projectid!;
  const isOpenConnectionInstructionModal =
    (
      useAppSelector((state) => state.modal).find((modal) => modal.type === ModalTypesEnum.CONNECTION_INSTRUCTIONS)
        ?.payload as ConnectionInstructionsProps['payload']
    )?.instructionsConfig?.instanceId === instanceId;
  const userId = useAppSelector(selectCurrentUserId);
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);

  const { data: projectData } = useGetProjectQuery({ projectId });
  const [startWorkbench, { isLoading: isStarting }] = useStartWorkbenchMutation({});
  const [stopWorkbench, { isLoading: isStopping }] = useStopWorkbenchMutation({});
  const [terminateWorkbench, { isLoading: isTerminating }] = useTerminateWorkbenchMutation({});
  const [getWorkbenchConnection, { isFetching: isFetchingConnectionInstructions }] =
    useLazyGetConnectToWorkbenchQuery();

  const userProjectRole = projectData?.members?.find(({ id }) => id === userId)?.role_id;
  const hasAccess =
    getPriviligies(userProjectRole)?.workbenchTab?.canOperate ||
    getPriviligies(userApplicationRole)?.workbenchTab?.canOperate;

  const isLoading = isStarting || isStopping || isTerminating || isFetchingConnectionInstructions;

  return (
    <Box display="flex" flexWrap="wrap" alignItems="center" height="100%">
      {status === WorkbenchStatusEnum.STOPPED && (
        <Tooltip title="Start" disableInteractive>
          <div>
            <IconButton
              disabled={isLoading || !hasAccess}
              onClick={() => {
                startWorkbench({ workbenchId, projectId });
              }}
            >
              {isStarting ? (
                <LoadingSpinner spinnerSize="xs" />
              ) : (
                <StartIcon fill={!hasAccess ? 'currentColor' : '#008EFF'} />
              )}
            </IconButton>
          </div>
        </Tooltip>
      )}
      {status === WorkbenchStatusEnum.RUNNING && (
        <Tooltip title="Stop" disableInteractive>
          <div>
            <IconButton
              disabled={isLoading || !hasAccess}
              onClick={() => {
                dispatch(
                  openModal({
                    type: ModalTypesEnum.CONFIRM,
                    payload: {
                      onSubmit: () => stopWorkbench({ workbenchId, projectId }),
                      confirmButtonLabel: 'Stop',
                      title: 'Stop this workbench?',
                      description:
                        'Stopping this workbench will shut down the EC2 instance associated with it. Data in persistent storage will be preserved and accessible once the workbench is restarted.',
                    },
                  }),
                );
              }}
            >
              {isStopping ? (
                <LoadingSpinner spinnerSize="xs" />
              ) : (
                <StopIcon fill={!hasAccess ? 'currentColor' : '#F7A800'} />
              )}
            </IconButton>
          </div>
        </Tooltip>
      )}
      {[WorkbenchStatusEnum.STOPPED, WorkbenchStatusEnum.RUNNING].includes(status) && (
        <Tooltip title="Terminate" disableInteractive>
          <div>
            <IconButton
              disabled={isLoading || !hasAccess}
              onClick={() => {
                dispatch(
                  openModal({
                    type: ModalTypesEnum.CONFIRM,
                    payload: {
                      onSubmit: () => terminateWorkbench({ workbenchId, projectId }),
                      confirmButtonLabel: 'Terminate',
                      title: (
                        <Stack direction="row" spacing={1}>
                          <Typography variant="bodyXl" color="error">
                            Terminate this workbench?
                          </Typography>
                          <WarningIcon fill="#E40A1A" />
                        </Stack>
                      ),
                      description: `Terminate the workbench ${workbenchName} will permanently remove it. Any data stored \n within workbench or the corresponsing S3 bucket will be lost unless saved to a Project S3 bucket.`,
                      confirmationLabel: 'Do you want to continue and terminate this workbench?',
                      showConfirmationControl: true,
                    },
                  }),
                );
              }}
            >
              {isTerminating ? (
                <LoadingSpinner spinnerSize="xs" />
              ) : (
                <TerminateIcon fill={!hasAccess ? 'currentColor' : '#E40A1A'} />
              )}
            </IconButton>
          </div>
        </Tooltip>
      )}
      {!isOpenConnectionInstructionModal && status === WorkbenchStatusEnum.RUNNING && (
        <Tooltip title="Connection Instructions" disableInteractive>
          <IconButton
            disabled={isLoading}
            onClick={async () => {
              const workbenchConnection = await getWorkbenchConnection({ workbenchId, projectId }).unwrap();

              dispatch(
                openModal({
                  type: ModalTypesEnum.CONNECTION_INSTRUCTIONS,
                  payload: {
                    instructionsConfig: {
                      awsRegion: workbenchConnection?.aws_region || '',
                      accessKeyId: workbenchConnection?.access_key_id || '',
                      secretAccessKey: workbenchConnection?.secret_access_key || '',
                      sessionToken: workbenchConnection?.session_token || '',
                      port: workbenchConnection?.port || DEFAULT_PORT,
                      instanceId,
                    },
                  },
                }),
              );
            }}
          >
            {isFetchingConnectionInstructions ? <LoadingSpinner spinnerSize="xs" /> : <InstructionsIcon />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default WorkbenchControlCell;
